// The Revenue tab, served from the rollup payload.
//
// Revenue was never what made this tab expensive: it reads the SAME rows
// Utilization does, so once medical_net_sales_amount rides along on MetricTotals
// there is nothing here a summed week cannot answer. Every figure below is
// buildTotalsFromRollup or buildDrillFromRollup output divided by another --
// deliberately, so the two tabs cannot come to disagree about which rows survive
// a filter.
//
// The one exception is a DVM row's sublabel, its highest-revenue site, which
// needs per-(doctor, site) revenue. That is the wide doctor rows' job, and they
// are present exactly when the drill level is DVM.

import { useMemo } from "react";
import type { FilterState, Granularity, ScopeFilters } from "@/types/filters";
import { makeTotalsReader, matchesScope } from "@/lib/rollupMetrics";
import { buildDrillFromRollup } from "@/lib/rollupDrill";
import { splitRange } from "@/lib/rollupSplit";
import { EMPTY_RANGE, getPeriodFamily, getPriorYearRange, type DateRange } from "@/lib/periods";
import { pinnedAncestorLevels, ancestorLabel, buildPartialScope } from "@/hooks/useDrillData";
import type { RollupPayload } from "@/hooks/useRollupData";
import type { RevenueKPIs, RevenueDrillRow, RevenuePinnedRow } from "@/hooks/useRevenueData";

const NO_SCOPE: ScopeFilters = {
  countryCode: null,
  divisionName: null,
  groupName: null,
  locationId: null,
  resourceId: null,
};

function forWindow(
  payload: RollupPayload,
  range: DateRange,
  g: Granularity,
  f: "trailing" | "calendar"
) {
  const split = splitRange(range, g, f);
  const weeks = new Set(split.wholeWeeks);
  const days = new Set(split.edgeDays);
  return {
    siteWeeks: payload.siteWeeks.filter((r) => weeks.has(String(r.week_start))),
    dvmWeeks: payload.dvmWeeks.filter((r) => weeks.has(String(r.week_start))),
    edgeRows: payload.edgeRows.filter((r) => days.has(r.schedule_date)),
  };
}

function rates(t: {
  totalMedRev: number;
  distinctDvms: number;
  allSeenMin: number;
  totalScheduledMin: number;
  totalCompleted: number;
  dvmDays: number;
}) {
  return {
    perDvm: t.distinctDvms > 0 ? t.totalMedRev / t.distinctDvms : 0,
    perSeenHour: t.allSeenMin > 0 ? t.totalMedRev / (t.allSeenMin / 60) : 0,
    perSchedHour: t.totalScheduledMin > 0 ? t.totalMedRev / (t.totalScheduledMin / 60) : 0,
    perAppt: t.totalCompleted > 0 ? t.totalMedRev / t.totalCompleted : 0,
    apptsPerDvmDay: t.dvmDays > 0 ? t.totalCompleted / t.dvmDays : 0,
  };
}

export interface RollupRevenue {
  kpis: RevenueKPIs;
  drillRows: RevenueDrillRow[];
  pinned: RevenuePinnedRow[];
}

export function useRollupRevenue(
  payload: RollupPayload | null,
  filters: FilterState,
  granularity: Granularity
): RollupRevenue | null {
  const { customStart, customEnd, period, scope, excludeZeroApptDays, drillLevel } = filters;
  return useMemo(() => {
    if (!payload) return null;

    const family = getPeriodFamily(period);
    const current: DateRange = { startDate: customStart, endDate: customEnd };
    const priorYear = getPriorYearRange(current, period) ?? EMPTY_RANGE;
    const cur = forWindow(payload, current, granularity, family);
    const py = forWindow(payload, priorYear, granularity, family);

    // makeTotalsReader owns the wide-vs-narrow rule; see rollupMetrics.ts.
    const totals = (part: ReturnType<typeof forWindow>, s: ScopeFilters) =>
      makeTotalsReader({
        part,
        payloadScope: scope,
        wideDvmRows: payload.wideDvmRows,
        excludeZeroApptDays,
      })(s);

    const c = rates(totals(cur, scope));
    const p = rates(totals(py, scope));
    const n = rates(totals(cur, NO_SCOPE));

    const kpis: RevenueKPIs = {
      medRevPerDvm: { current: c.perDvm, priorYear: p.perDvm, nva: n.perDvm },
      medRevPerSeenHour: { current: c.perSeenHour, priorYear: p.perSeenHour, nva: n.perSeenHour },
      medRevPerSchedHour: {
        current: c.perSchedHour,
        priorYear: p.perSchedHour,
        nva: n.perSchedHour,
      },
      medRevPerAppt: { current: c.perAppt, priorYear: p.perAppt, nva: n.perAppt },
      apptsPerDvmDay: {
        current: c.apptsPerDvmDay,
        priorYear: p.apptsPerDvmDay,
        nva: n.apptsPerDvmDay,
      },
    };

    // A DVM row is sublabelled with the site where they earned most, formatted
    // like a Site drill label so the two levels read the same.
    const primarySite = new Map<string, string>();
    if (drillLevel === "dvm") {
      // Names come from BOTH sources. A day-granularity period has no site-week
      // rows at all -- T1W is answered entirely from raw edge rows -- so reading
      // only siteWeeks left every sublabel as a bare "1279 - ".
      const siteNames = new Map<string, string>();
      for (const s of payload.siteWeeks) {
        siteNames.set(String(s.location_id), String(s.location_name ?? ""));
      }
      for (const r of payload.edgeRows) {
        if (!siteNames.has(r.location_id)) siteNames.set(r.location_id, r.location_name);
      }
      const byDvmSite = new Map<string, number>();
      const bump = (dvm: string, site: string, rev: number) => {
        const k = dvm + "|" + site;
        byDvmSite.set(k, (byDvmSite.get(k) ?? 0) + rev);
      };
      for (const d of cur.dvmWeeks) {
        if (!matchesScope(d, scope)) continue;
        const row = d as unknown as Record<string, unknown>;
        const rev = Number(
          excludeZeroApptDays ? row.medical_net_sales_amount_nonzero : row.medical_net_sales_amount
        );
        bump(String(d.resource_id), String(d.location_id), rev || 0);
      }
      for (const r of cur.edgeRows) {
        if (!matchesScope(r, scope)) continue;
        // Raw rows carry no _nonzero columns; the exclusion is applied here.
        if (excludeZeroApptDays && Number(r.completed_appointment_minutes) <= 0) continue;
        bump(r.resource_id, r.location_id, Number(r.medical_net_sales_amount) || 0);
      }
      const best = new Map<string, { site: string; rev: number; label: string }>();
      for (const [key, rev] of byDvmSite) {
        const sep = key.indexOf("|");
        const dvm = key.slice(0, sep);
        const site = key.slice(sep + 1);
        const held = best.get(dvm);
        // Compared by the rendered label, matching the raw path.
        const label = site + " - " + (siteNames.get(site) ?? "");
        if (!held || rev > held.rev || (rev === held.rev && label < held.label)) {
          best.set(dvm, { site, rev, label });
        }
      }
      for (const [dvm, held] of best) {
        primarySite.set(dvm, held.label);
      }
    }

    const pyRevById = new Map<string, number>();
    for (const e of buildDrillFromRollup({ ...py, scope, drillLevel, excludeZeroApptDays })) {
      pyRevById.set(e.id, e.totals.totalMedRev);
    }

    const drillRows: RevenueDrillRow[] = buildDrillFromRollup({
      ...cur,
      scope,
      drillLevel,
      excludeZeroApptDays,
    })
      .map((e) => ({
        id: e.id,
        label: e.label,
        subLabel: drillLevel === "dvm" ? (primarySite.get(e.id) ?? null) : null,
        scheduledDays: e.totals.distinctDates,
        dvmDays: e.totals.dvmDays,
        scheduledMinutes: e.totals.totalScheduledMin,
        apptMinutes: e.totals.allSeenMin,
        completedAppts: e.totals.totalCompleted,
        medicalRevenue: e.totals.totalMedRev,
        pyMedicalRevenue: pyRevById.get(e.id) ?? null,
      }))
      .sort((a, b) => b.medicalRevenue - a.medicalRevenue);

    const pinned: RevenuePinnedRow[] = pinnedAncestorLevels(drillLevel, scope).map((level) => {
      const partial = buildPartialScope(scope, level);
      const t = totals(cur, partial);
      const pt = totals(py, partial);
      return {
        label: ancestorLabel(level, scope),
        dvmDays: t.dvmDays,
        scheduledMinutes: t.totalScheduledMin,
        apptMinutes: t.allSeenMin,
        completedAppts: t.totalCompleted,
        medicalRevenue: t.totalMedRev,
        // null, not 0, when the ancestor has nothing in the prior-year window:
        // the column renders a dash rather than a -100%.
        pyMedicalRevenue: pt.dvmDays > 0 ? pt.totalMedRev : null,
      };
    });

    return { kpis, drillRows, pinned };
  }, [
    payload,
    granularity,
    customStart,
    customEnd,
    period,
    scope,
    excludeZeroApptDays,
    drillLevel,
  ]);
}
